/**
 * EffectiveSLP — kid-facing practice player scoring functions
 * (fix #252 / #PL-WEB-RUBRICS).
 *
 * Every archetype rubric has a dedicated `score<Archetype>` here.
 * Each one is PURE — no DOM, no React, no IndexedDB — so it can
 * be unit-tested directly (AC #6) and re-used on the server side
 * later if needed.
 *
 * Contract (AC #4 in the issue body):
 *
 *   score<Archetype>(response, rubric, meta) → {
 *     is_correct: boolean;
 *     partial_credit?: number;        // 0..1, present when an in-between
 *                                    // tier exists (only conversation_turns).
 *     common_wrong_answers?: string[]; // selected common-wrong choices;
 *                                     // empty by default.
 *   }
 *
 * `meta` carries player-side flags (`self_corrected`, `used_cue`).
 * Reserved for v1.1 cue-aware scoring; current scoring rules do
 * not act on them because the renderer records self_corrected /
 * used_cue directly into the `practice_attempts` queue row.
 * Keeping the uniform `(response, rubric, meta)` signature lets
 * the dispatcher call every scorer through the same call site.
 *
 * Scoring rules per archetype (per the issue body's AC #2):
 *
 *   receptive_directions: exact ordered-sequence match. Self-
 *     correction does NOT downgrade a correct final sequence to
 *     partial credit — the kid landed where they needed to.
 *
 *   sentence_completion: per-slot exact match; a slot matches if
 *     the response word equals `expected[i]` case-insensitively
 *     OR appears in `alternates?.[i]`. is_correct = every slot
 *     matches.
 *
 *   conversation_turns: matches by `tag`. correct → is_correct
 *     true + partial_credit 1. partial → is_correct false +
 *     partial_credit 0.5. wrong → is_correct false + partial
 *     credit 0.
 *
 *   sequencing: positional permutation match. If
 *     `one_swap_tolerance` is on, swapping two adjacent cards
 *     in the kid's arrangement still counts as full credit.
 *
 *   picture_naming: included so the dispatcher can fall back to
 *     it for the #251 archetype. Same case-insensitive equality
 *     rule the existing inline player uses.
 */

import type {
  ConversationTurnsRubric,
  ReceptiveDirectionsRubric,
  SequencingRubric,
  SentenceCompletionRubric,
} from './rubrics';

/** Shared scoring output shape (AC #4 in the issue body). */
export interface ScoringResult {
  is_correct: boolean;
  /** 0..1 — only set for archetypes with in-between tiers. */
  partial_credit?: number;
  /** Tag/category of the most common wrong answer (if any). */
  common_wrong_answers?: string[];
}

/** Player-supplied metadata. `used_cue` and `self_corrected` are
 *  boolean flags the renderer passes through. */
export interface ScoringMeta {
  self_corrected?: boolean;
  used_cue?: boolean;
  /** response_latency_ms — included for parity with QueuedAttempt. */
  latency_ms?: number;
}

/* ------------------------------------------------------------------------- */
/* picture_naming                                                            */
/* ------------------------------------------------------------------------- */

/**
 * Picture-naming scorer. Pass `response` as a string the kid
 * tapped; `expected` is the canonical word in the rubric. The
 * existing #251 player used `selectedChoice.toLowerCase() ===
 * trial.expected.toLowerCase()` directly; we centralize that rule
 * here so future archetypes stay comparable.
 *
 * `meta` is reserved for the renderer to thread `self_corrected`
 * and `used_cue` through the same call site as the score; it is
 * currently unused here because the queue row stores those
 * fields directly. Reserved for v1.1 SLP-tuning A/B tests.
 */
export function scorePictureNaming(
  response: string,
  expected: string,
  _meta: ScoringMeta = {},
): ScoringResult {
  const ok =
    typeof response === 'string' &&
    response.toLowerCase() === expected.toLowerCase();
  return {
    is_correct: ok,
    common_wrong_answers: ok ? [] : [response],
  };
}

/* ------------------------------------------------------------------------- */
/* receptive_directions                                                      */
/* ------------------------------------------------------------------------- */

/**
 * Receptive-directions scorer. The kid taps a sequence of 0-based
 * grid indices; `response` is the ordered array they actually tapped
 * (same length as `rubric.expected_sequence` typically). Exact
 * ordered match required.
 */
export function scoreReceptiveDirections(
  response: readonly number[],
  rubric: ReceptiveDirectionsRubric,
  _meta: ScoringMeta = {},
): ScoringResult {
  const expected = rubric.expected_sequence;
  const len = expected.length;
  const resp = response;
  if (resp.length !== len) {
    return {
      is_correct: false,
      common_wrong_answers: ['wrong_length'],
    };
  }
  let allMatch = true;
  for (let i = 0; i < len; i += 1) {
    if (resp[i] !== expected[i]) {
      allMatch = false;
      break;
    }
  }
  return {
    is_correct: allMatch,
    common_wrong_answers: allMatch ? [] : ['wrong_order'],
  };
}

/* ------------------------------------------------------------------------- */
/* sentence_completion                                                       */
/* ------------------------------------------------------------------------- */

/**
 * Sentence-completion scorer. `response` is the kid's placed
 * word in slot order; per-slot exact (case-insensitive) match
 * against `expected[i]` OR `alternates?.[i]`.
 */
export function scoreSentenceCompletion(
  response: readonly string[],
  rubric: SentenceCompletionRubric,
  _meta: ScoringMeta = {},
): ScoringResult {
  const expected = rubric.expected;
  const len = expected.length;
  const resp = response;
  if (resp.length !== len) {
    return {
      is_correct: false,
      common_wrong_answers: ['wrong_length'],
    };
  }
  let allMatch = true;
  const wrong: string[] = [];
  for (let i = 0; i < len; i += 1) {
    const got = (resp[i] ?? '').toLowerCase();
    const want = (expected[i] ?? '').toLowerCase();
    const alts = (rubric.alternates?.[i] ?? []).map((s) => s.toLowerCase());
    if (got !== want && !alts.includes(got)) {
      allMatch = false;
      wrong.push(got);
    }
  }
  return {
    is_correct: allMatch,
    common_wrong_answers: wrong,
  };
}

/* ------------------------------------------------------------------------- */
/* conversation_turns                                                        */
/* ------------------------------------------------------------------------- */

/**
 * Conversation-turns scorer. `response` is the 0-indexed option
 * the kid tapped. Result tiers:
 *   correct → is_correct true,  partial_credit 1
 *   partial → is_correct false, partial_credit 0.5
 *   wrong   → is_correct false, partial_credit 0
 */
export function scoreConversationTurns(
  response: number,
  rubric: ConversationTurnsRubric,
  _meta: ScoringMeta = {},
): ScoringResult {
  const opt = rubric.options[response];
  if (!opt) {
    return {
      is_correct: false,
      partial_credit: 0,
      common_wrong_answers: ['out_of_range'],
    };
  }
  switch (opt.tag) {
    case 'correct':
      return {
        is_correct: true,
        partial_credit: 1,
        common_wrong_answers: [],
      };
    case 'partial':
      return {
        is_correct: false,
        partial_credit: 0.5,
        common_wrong_answers: ['partial'],
      };
    case 'wrong':
      return {
        is_correct: false,
        partial_credit: 0,
        common_wrong_answers: ['wrong'],
      };
  }
}

/* ------------------------------------------------------------------------- */
/* sequencing                                                                */
/* ------------------------------------------------------------------------- */

/**
 * Sequencing scorer. `response` is the kid's arrangement — an
 * ordered array of card indices (0..3) referencing cards in
 * `rubric.cards`. The canonical correct order is `positions` of
 * the cards sorted by `position`. With `one_swap_tolerance` on,
 * swapping two adjacent cards in the response still counts as
 * correct.
 */
export function scoreSequencing(
  response: readonly number[],
  rubric: SequencingRubric,
  _meta: ScoringMeta = {},
): ScoringResult {
  // Build the canonical order: for each slot, which card index belongs
  // there, sorted by the card's canonical `position`.
  const expectedOrder = [...rubric.cards]
    .map((card, index) => ({ index, position: card.position }))
    .sort((a, b) => a.position - b.position)
    .map((item) => item.index);
  const resp = response;
  if (resp.length !== 4) {
    return {
      is_correct: false,
      common_wrong_answers: ['wrong_length'],
    };
  }
  // Validate response indices are a permutation of {0,1,2,3}.
  const seen = new Set<number>();
  for (const i of resp) {
    if (!Number.isInteger(i) || i < 0 || i > 3 || seen.has(i)) {
      return {
        is_correct: false,
        common_wrong_answers: ['invalid_indices'],
      };
    }
    seen.add(i);
  }
  if (seen.size !== 4) {
    return {
      is_correct: false,
      common_wrong_answers: ['invalid_indices'],
    };
  }
  // Exact permutation match.
  if (resp.every((cardIdx, slot) => cardIdx === expectedOrder[slot])) {
    return { is_correct: true, common_wrong_answers: [] };
  }
  if (!rubric.one_swap_tolerance) {
    return { is_correct: false, common_wrong_answers: ['wrong_order'] };
  }
  // One-swap tolerance: count adjacent swaps the kid made.
  // A single adjacent swap yields exactly 2 mismatches whose
  // indices differ by 1; multiple disjoint swaps or a non-
  // adjacent discrepancy fail this branch.
  const mismatches: number[] = [];
  for (let i = 0; i < 4; i += 1) {
    if (resp[i] !== expectedOrder[i]) mismatches.push(i);
  }
  if (
    mismatches.length === 2 &&
    Math.abs(mismatches[0]! - mismatches[1]!) === 1
  ) {
    return { is_correct: true, common_wrong_answers: ['one_swap'] };
  }
  return { is_correct: false, common_wrong_answers: ['wrong_order'] };
}
